import React, { useCallback, useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { goalModesService, GoalMode, GoalModesView } from '../../services/goalModesService';

const MODES: GoalMode[] = ['off', 'shadow', 'live'];
const LABEL: Record<GoalMode, string> = { off: 'Off', shadow: 'Shadow', live: 'Live' };

interface Props {
  sessionId: string;
  canEdit?: boolean;
}

/** Off, shadow or live for each goal the intent router may start (agentic core R1a, WS6). */
const GoalModesPanel: React.FC<Props> = ({ sessionId, canEdit = true }) => {
  const [view, setView] = useState<GoalModesView | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const next = await goalModesService.get(sessionId);
      setView({ ...next, goals: Array.isArray(next?.goals) ? next.goals : [] });
    } catch {
      /* the panel stays hidden until the view loads */
    }
  }, [sessionId]);

  useEffect(() => {
    load();
  }, [load]);

  const choose = async (goalId: string, mode: GoalMode) => {
    setBusy(goalId);
    setError(null);
    try {
      const next = await goalModesService.set(sessionId, goalId, mode);
      setView({ ...next, goals: Array.isArray(next?.goals) ? next.goals : [] });
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Could not change the goal');
    } finally {
      setBusy(null);
    }
  };

  if (!view || !view.goals.length) return null;

  return (
    <div className="flex flex-col gap-2 text-left" data-testid="goal-modes">
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-bold text-primary tracking-widest uppercase">Platform goals</span>
        {!view.enabled && <span className="text-[10px] font-semibold text-red-600">Switched off for every project</span>}
      </div>
      {error && <p className="text-xs text-red-600" role="alert">{error}</p>}
      <ul className="flex flex-col divide-y divide-[#e2e8f0] border border-[#e2e8f0] rounded-xl bg-white">
        {view.goals.map((g) => (
          <li key={g.goalId} className="flex items-center gap-3 px-3 py-2">
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-navy">{g.goalId}</p>
              <p className="text-[10px] text-slate-400 truncate" title={g.when}>{g.killed ? 'Switched off for every project' : g.when}</p>
            </div>
            {busy === g.goalId && <Loader2 size={12} className="animate-spin text-slate-400" />}
            <div role="radiogroup" aria-label={`${g.goalId} mode`} className="flex rounded-lg border border-[#e2e8f0] overflow-hidden shrink-0">
              {MODES.map((m) => (
                <button
                  key={m}
                  type="button"
                  role="radio"
                  aria-checked={g.mode === m}
                  disabled={!canEdit || g.killed || busy === g.goalId}
                  onClick={() => g.mode !== m && choose(g.goalId, m)}
                  className={`px-2 py-0.5 text-[11px] font-semibold transition-colors disabled:opacity-60 ${
                    g.mode === m ? 'bg-primary text-white' : 'bg-white text-slate-500 hover:bg-slate-50'
                  }`}
                >
                  {LABEL[m]}
                </button>
              ))}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default GoalModesPanel;
